import type { ScaleLinear, ScaleTime } from "d3-scale";
import { createContext, useContext, useEffect, useRef } from "react";
import type { BaseDataPoint, TooltipBaseline } from "../utils/charts";
import type { ComponentFormatter } from "../utils/formatting";

/**
 * Chart Y-axis formatter type
 * Uses the main formatting system for consistency with Kpi, StatList, and DataTable
 */
export type YFormatType = ComponentFormatter;

export interface TooltipData {
  /** The x-axis value (Date for time-series charts) */
  xValue: Date;
  series: TooltipSeries[];
  x: number;
  y: number;
  position: "left" | "right";
  /** Whether this tooltip is for an event marker (vs data point) */
  isEvent?: boolean;
  /** Optional event metadata to display */
  eventMetadata?: Record<string, string | number>;
  /** Optional list of event items to display in event tooltips. */
  eventItems?: TooltipEventItem[];
}

interface TooltipEventItem {
  label: string;
  color: string;
  metadata?: Record<string, string | number>;
}

export interface TooltipSeries {
  label: string;
  xValue?: Date;
  value: number;
  /**
   * Lower bound for a min–max band series. When set, the tooltip renders the
   * range `baselineValue–value` instead of a single value.
   */
  baselineValue?: number;
  /**
   * Optional y-axis coordinate (in data space) where the focus highlight
   * should be drawn for this series. Defaults to `value`. Used by stacked
   * series where the drawn position differs from the reported value.
   */
  chartY?: number;
  showMarker?: boolean;
  color: string;
  type?: "area" | "line" | "bar" | "point" | "event";
  category?: string;
  categoryColor?: string;
  /** Optional extra rows to display in the tooltip below the category row. */
  extraTooltipRows?: Array<{ label: string; value: string }>;
}

interface SeriesMetadata {
  label: string;
  color: string;
  /**
   * Set only by a banded `AreaSeries` that opted into the min-max range tooltip.
   * `ChartContainer` has always put these on the metadata it provides; they were
   * simply not declared, so anything reading series off the context built tooltip
   * rows that silently dropped the lower bound.
   */
  baseline?: TooltipBaseline;
  tooltipRange?: boolean;
}

interface ChartContextType {
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  width: number;
  height: number;
  yFormatter: YFormatType;
  animationSettings: {
    duration: number;
    ease: string;
  };
  tooltip: {
    data: TooltipData | null;
    show: (data: TooltipData) => void;
    hide: () => void;
  };
  /** Series datasets for accessing underlying data */
  datasets?: BaseDataPoint[][];
  /** Series metadata for labels and colors */
  seriesMetadata?: SeriesMetadata[];
  /** Series types for proper tooltip rendering */
  seriesTypes?: ("area" | "line" | "bar" | "point")[];
  /** Category colors for bar charts */
  categoryColors?: Record<string, string>;
  /** Category labels for translating category values */
  categoryLabels?: Record<string, string>;
  /** Set by the engines: whether the chart has already played its entrance (see `useChartEntrance`). */
  entrance?: { played: boolean };
}

export const defaultAnimationSettings = {
  duration: 0.5,
  ease: "easeInOut",
};

export const ChartContext = createContext<ChartContextType | null>(null);

/**
 * Has this chart already played its series entrance animation?
 *
 * A hidden series is unmounted, so re-enabling it from the legend mounts a fresh
 * component and the scaleY grow-in replays — the geometry re-animates on every
 * toggle, and again on every resize that remounts the zoom subtree.
 *
 * A ref rather than state: flipping it must not re-render, because it decides
 * nothing about the current frame — only what a *later* mount does. Series in the
 * first commit all read `false`, since effects run after every render in a commit.
 * Both engines call this and pass the result down as `entrance`; a series rendered
 * without it animates, as it always has.
 */
export function useChartEntrance(): { played: boolean } {
  const entrance = useRef({ played: false });
  useEffect(() => {
    entrance.current.played = true;
  }, []);
  return entrance.current;
}

export type { ChartContextType };

export const useChartContext = () => {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error("useChartContext must be used within a ChartContextProvider");
  }
  return context;
};
